import { useRef, useState, useEffect, useCallback } from "react";

export default function useLiveness(settings = {}) {
  const {
    throttleMs = 60,
    livenessFrames = 4, // 4 frames balances speed and security
    cooldownMs = 4000,
    enableLiveness = false,
  } = settings;

  const lastRunRef = useRef(0);
  const cooldownRef = useRef(0);
  const stableLiveRef = useRef(0);
  const consecutiveMissesRef = useRef(0); 
  const statusRef = useRef("📷 Position your face front");
  
  const [livenessStatus, setLivenessStatus] = useState("");
  const [progress, setProgress] = useState(0);

  const resetLiveness = useCallback(() => {
    stableLiveRef.current = 0;
    consecutiveMissesRef.current = 0;
    setProgress(0);
    statusRef.current = "📷 Position your face front";
  }, []);

  const isLive = useCallback((detection) => {
    if (!enableLiveness) return !!detection;

    const now = performance.now();
    if (now - lastRunRef.current < throttleMs) return false;
    lastRunRef.current = now;

    if (!detection) {
      resetLiveness();
      return false;
    }

    // 1. Minimum Face Size Requirement
    if (detection.detection.box.width < 100) {
      statusRef.current = "📷 Move closer";
      resetLiveness();
      return false;
    }

    const landmarks = detection.landmarks;
    const nose = landmarks.getNose();
    const jaw = landmarks.getJawOutline();
    const leftEye = landmarks.getLeftEye();
    const rightEye = landmarks.getRightEye();

    // 2. Structural Face-Front Metrics
    const noseTip = nose[3];
    
    // Horizontal Check: Distance from nose tip to left jaw vs right jaw
    const distToLeftJaw = Math.abs(noseTip.x - jaw[0].x);
    const distToRightJaw = Math.abs(jaw[16].x - noseTip.x);
    const horizontalAsymmetry = Math.abs(distToLeftJaw - distToRightJaw);

    // Vertical Check: Distance from nose tip to eyes line vs nose tip to chin
    const eyeCenterY = (leftEye[0].y + rightEye[3].y) / 2;
    const chinY = jaw[8].y;
    const upperFaceDist = Math.abs(noseTip.y - eyeCenterY);
    const lowerFaceDist = Math.abs(chinY - noseTip.y);
    const verticalAsymmetry = Math.abs(upperFaceDist - lowerFaceDist * 0.75); // Normalized scalar

    // Roll Check: Horizontal tilt between the left and right eyes
    const eyeTilt = Math.abs(leftEye[0].y - rightEye[3].y);

    // Adaptive thresholds (wider tolerance limits to prevent getting stuck)
    const isTracking = stableLiveRef.current > 0;
    const yawLimit = isTracking ? 35 : 25;   // Side-to-side pixel deviance
    const pitchLimit = isTracking ? 40 : 30; // Up-down pixel deviance
    const rollLimit = isTracking ? 20 : 14;  // Tilting pixel deviance

    const isYawStraight = horizontalAsymmetry < yawLimit;
    const isPitchStraight = verticalAsymmetry < pitchLimit;
    const isRollLevel = eyeTilt < rollLimit;

    // Evaluate alignment
    if (!isYawStraight || !isPitchStraight || !isRollLevel) {
      consecutiveMissesRef.current++;
      
      // Let the status stick for 2 frames before throwing structural error directions
      if (consecutiveMissesRef.current > 2) {
        if (!isRollLevel) {
          statusRef.current = "🔄 Level your head";
        } else if (!isYawStraight) {
          statusRef.current = "👁️ Look directly front";
        } else {
          statusRef.current = "↕️ Look straight ahead";
        }
        stableLiveRef.current = 0;
        setProgress(0);
      }
      return false;
    }

    // Clear tracking drop counters on a healthy match
    consecutiveMissesRef.current = 0;

    // 3. Increment Verification State
    stableLiveRef.current++;
    const currentProgress = Math.round((stableLiveRef.current / livenessFrames) * 100);
    setProgress(Math.min(100, currentProgress));

    if (stableLiveRef.current >= livenessFrames) {
      statusRef.current = "✅ Success!"; 
      return true;
    }

    statusRef.current = "🙂 Hold steady...";
    return false;
  }, [enableLiveness, livenessFrames, throttleMs, resetLiveness]);

  const confirmMatch = useCallback((detection) => {
    const now = performance.now();
    
    if (now < cooldownRef.current) {
      statusRef.current = "⏳ Please wait...";
      return false;
    }

    const passed = isLive(detection);

    if (passed) {
      cooldownRef.current = now + cooldownMs;
      statusRef.current = "✅ Verified!";
      resetLiveness();
      return true;
    }

    return false;
  }, [isLive, cooldownMs, resetLiveness]);

  useEffect(() => {
    const id = setInterval(() => {
      setLivenessStatus(statusRef.current);
    }, 50);
    return () => clearInterval(id);
  }, []);

  return { isLive, confirmMatch, livenessStatus, progress, resetLiveness };
}